/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  CompleteEnvironmentType,
  DefaultEnvironment,
  Environment,
  EnvironmentContext,
  RootEnvironment
} from './EnvironmentContext';
import { useContext, useMemo } from 'preact/hooks';

import { ComponentChildren } from 'preact';
import { LayerManager } from '../UNSAFE_Layer/LayerManager';

// EnvironmentProvider properties
export declare type ProviderProperties<Env extends RootEnvironment | Environment> = {
  environment?: Env;
  children?: ComponentChildren;
};

// Custom merge function
function mergeEnvironment(
  env1: CompleteEnvironmentType,
  env2: Partial<RootEnvironment> | undefined
) {
  const userValue = Object.assign({}, env1.user, env2?.user);
  const themeValue = Object.assign({}, env1.theme, env2?.theme);

  // Merge translation values - one level merge
  const targetTranslations = Object.assign({}, env1.translations);
  const sourceTranslations = env2?.translations || {};
  Object.keys(sourceTranslations).forEach((key) => {
    let newVal = sourceTranslations[key];
    if (targetTranslations[key]) {
      // merge is needed
      newVal = Object.assign({}, targetTranslations[key], newVal);
    }
    targetTranslations[key] = newVal;
  });

  return {
    user: userValue,
    theme: themeValue,
    translations: targetTranslations,
    colorScheme: env2?.colorScheme ?? env1.colorScheme,
    scale: env2?.scale ?? env1.scale,
    currentBgColor: env2?.currentBgColor ?? env1.currentBgColor,
    mode: env2?.mode ?? env1.mode
  };
}

/**
 * The RootEnvironmentProvider component bootstraps essential services and
 * must be placed at the root of any application using components from '@oracle/oraclejet-preact'.
 * Minimally, the application must load the appropriate translation bundle for the user's locale
 * and set it on the RootEnvironmentProvider.
 */
export function RootEnvironmentProvider({
  children,
  environment
}: ProviderProperties<RootEnvironment>) {
  const mergedEnvironment = useMemo(
    () => mergeEnvironment(DefaultEnvironment, environment),
    [environment]
  );
  return (
    <EnvironmentContext.Provider value={mergedEnvironment}>
      <LayerManager>{children}</LayerManager>
    </EnvironmentContext.Provider>
  );
}

/**
 * The EnvironmentProvider is a component that should be used by the application when there is a need to overwrite
 * environment values for a subtree.
 * The component receives an Environment object that will be merged into the values provided by the nearest ancestor Provider.
 * The new environment will be passed to the component's children.
 * Note that some environment values cannot be overwritten. See the description of the Environment type for the list of values
 * that can be replaced.
 */
export function EnvironmentProvider({ children, environment }: ProviderProperties<Environment>) {
  const consumerEnv = useContext(EnvironmentContext);
  const mergedEnvironment = useMemo(
    () => mergeEnvironment(consumerEnv, environment),
    [consumerEnv, environment]
  );

  return (
    <EnvironmentContext.Provider value={mergedEnvironment}>{children}</EnvironmentContext.Provider>
  );
}
